import {
  LabeledValue,
  LabeledValueWithButton,
  ModalOpenButton,
} from "@msys/ui";
import {
  Delete as DeleteIcon,
  Functions as FunctionsIcon,
} from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { EntitySearchFilterOperator } from "../../../clients/graphqlTypes.js";
import type { PropertyOption } from "./modals/ExpressionInputWithVariables.js";
import { SearchFilterExpressionModal } from "./modals/SearchFilterExpressionModal.js";

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  label: string;
  /** The row's rendered value — its operator and value, or the placeholder. */
  value: string;
  /** Whether an expression drives the value, from the slot's `__typename`. */
  isComputed: boolean;
  /** The current expression, empty on an authored row. */
  expression: string;
  operator: EntitySearchFilterOperator;
  operatorOptions: { label: string; value: EntitySearchFilterOperator }[];
  readOnly?: boolean;
  onSaveExpression: (
    operator: EntitySearchFilterOperator,
    expr: string
  ) => Promise<void>;
  /** Detach the expression, keeping the filter at its last computed value. */
  onRemoveExpression: () => Promise<void>;
  /**
   * Remove the filter outright — slot and expression together. Present only
   * where the row itself deletes: the recommendation modal, whose rows are
   * buffered in Formik. The boxes delete from their search-filter modal, so
   * the row carries its value control alone.
   */
  onDeleteFilter?: () => Promise<void>;
  /**
   * The authored value control for this row, rendered in place of the
   * read-only value. Absent on a row whose value cannot be authored inline
   * (the product box's Id filters).
   */
  valueInput?: React.ReactNode;
  /**
   * Narrow the variables the formula may read. The placeholder context passes
   * its own interface properties out: a filter selects the template that fills
   * them, so reading one closes the cycle property → selection → filter that
   * the dependency graph rejects on save (MEIS-13007).
   */
  filterOptions?: (option: PropertyOption) => boolean;
  /**
   * Wrap the expression affordances in the site's capability/permission gate,
   * rendering `fallback` when it denies. A denied computed row still shows its
   * value — read-only — which is why the gate takes the fallback rather than
   * swallowing the whole row.
   */
  renderExpressionGate?: (
    gated: React.ReactNode,
    fallback: React.ReactNode
  ) => React.ReactNode;
}

const defaultGate = (gated: React.ReactNode) => gated;

/**
 * One row of a merged search-filter section: an authored filter with an `fx`
 * affordance that attaches an expression, or an expression-driven one showing
 * its last computed value read-only. Purely presentational — the clause-shaped
 * knowledge of how to build a write entry stays with the caller, so the same
 * row serves property filters and the product box's named filters alike.
 */
export function SearchFilterField({
  projectId,
  docId,
  itemId,
  label,
  value,
  isComputed,
  expression,
  operator,
  operatorOptions,
  readOnly = false,
  onSaveExpression,
  onRemoveExpression,
  onDeleteFilter,
  valueInput,
  filterOptions,
  renderExpressionGate = defaultGate,
}: Props) {
  const { t } = useTranslate(["QuoteItem"]);

  const expressionModalProps = {
    projectId,
    docId,
    itemId,
    filterLabel: label,
    operator,
    allowedSearchFilterOperators: operatorOptions,
    expression,
    filterOptions,
    handleComplete: async (
      next: { expr: string; operator: EntitySearchFilterOperator },
      handleClose: () => void
    ) => {
      await onSaveExpression(next.operator, next.expr);
      handleClose();
    },
    // Only an expression-driven row has something to detach, and detaching
    // keeps the filter carrying whatever the expression last computed.
    handleDelete: isComputed
      ? async (handleClose: () => void) => {
          await onRemoveExpression();
          handleClose();
        }
      : undefined,
  };

  const readOnlyComputedValue = (
    <LabeledValue label={label} labelIcon={FunctionsIcon}>
      {value}
    </LabeledValue>
  );

  const deleteButton =
    !readOnly && onDeleteFilter ? (
      <IconButton
        size="small"
        color="primary"
        aria-label={t("Delete filter {label}", {
          ns: "QuoteItem",
          label,
        })}
        onClick={onDeleteFilter}
      >
        <DeleteIcon />
      </IconButton>
    ) : null;

  return (
    <Stack
      direction={"row"}
      spacing={0.5}
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      {isComputed ? (
        readOnly ? (
          readOnlyComputedValue
        ) : (
          renderExpressionGate(
            <ModalOpenButton
              Modal={SearchFilterExpressionModal}
              modalProps={expressionModalProps}
            >
              <LabeledValueWithButton label={label} labelIcon={FunctionsIcon}>
                {value}
              </LabeledValueWithButton>
            </ModalOpenButton>,
            readOnlyComputedValue
          )
        )
      ) : valueInput && !readOnly ? (
        valueInput
      ) : (
        <LabeledValue label={label}>{value}</LabeledValue>
      )}

      <Stack direction={"row"} spacing={0.5} sx={{ alignItems: "center" }}>
        {!isComputed &&
          !readOnly &&
          renderExpressionGate(
            <ModalOpenButton
              Modal={SearchFilterExpressionModal}
              modalProps={expressionModalProps}
            >
              <IconButton
                size="small"
                color="secondary"
                aria-label={t("Add formula to {label}", {
                  ns: "QuoteItem",
                  label,
                })}
              >
                <FunctionsIcon />
              </IconButton>
            </ModalOpenButton>,
            null
          )}
        {deleteButton &&
          /* Deleting a computed row removes its expression along with the
             slot, which the merged mutation rejects without expression
             access — so the action is gated like the expression ones. */
          (isComputed
            ? renderExpressionGate(deleteButton, null)
            : deleteButton)}
      </Stack>
    </Stack>
  );
}
